const _isDev =
  window.location.hostname === 'localhost' ||
  new URLSearchParams(window.location.search).has('preview_theme_id');

class FacetShort extends HTMLSelectElement {
  constructor() {
    super();
    this.initMotionInView();

    // Store bound listener for proper cleanup
    this.boundOnSelectChange = this.onSelectChange.bind(this);
    this.addEventListener('change', this.boundOnSelectChange);
  }

  connectedCallback() {
    // Drawer-hosted facets get moved to document.body on first open
    // (DrawerComponent.requiresBodyAppended), which disconnects then
    // reconnects this element. Re-attach so the drawer copy still works.
    this.addEventListener('change', this.boundOnSelectChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.boundOnSelectChange);
  }

  get options() {
    return this.getElementsByTagName('option');
  }

  get fakeSelectElement() {
    return this.previousElementSibling;
  }

  initMotionInView() {
    FoxTheme.Motion.inView(this, this.calcSelectWidth.bind(this), {
      margin: '200px 0px 200px 0px',
    });
  }

  calcSelectWidth() {
    const selectStyle = window.getComputedStyle(this);
    const selectedOptionText = this.options[this.selectedIndex].text;

    const textElement = this.createTextElement(selectedOptionText, selectStyle);
    document.body.appendChild(textElement);
    const selectWidth = textElement.offsetWidth;
    textElement.remove();

    this.style.setProperty('--width', `${selectWidth + 22}px`);
  }

  createTextElement(text, style) {
    const textElement = document.createElement('span');
    textElement.style.fontFamily = style.fontFamily;
    textElement.style.fontSize = style.fontSize;
    textElement.style.fontWeight = style.fontWeight;
    textElement.style.visibility = 'hidden';
    textElement.style.position = 'absolute';
    // Use textContent for plain text (safer than innerHTML)
    textElement.textContent = text;
    return textElement;
  }

  onSelectChange(event) {
    this.calcSelectWidth();
    this.updateFormParams(event);
  }

  updateFormParams(event) {
    const form = this.getClosestFacetForm() || this.getFirstFacetForm();

    if (form) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      url.searchParams.delete('page');
      form.renderSection(url.toString(), event);
    }
  }

  getClosestFacetForm() {
    return this.closest('form[is="facet-form"]');
  }

  getFirstFacetForm() {
    return document.querySelector('form[is="facet-form"]');
  }
}

customElements.define('facet-short', FacetShort, { extends: 'select' });
class PriceRange extends HTMLElement {
  constructor() {
    super();

    this.minRangeInput = this.querySelector('input[type="range"]:first-child');
    this.maxRangeInput = this.querySelector('input[type="range"]:last-child');
    this.minPriceInput = this.querySelector('input[name="filter.v.price.gte"]');
    this.maxPriceInput = this.querySelector('input[name="filter.v.price.lte"]');

    // Store bound listeners for proper cleanup
    this.boundMinPriceSelect = this.minPriceInput.select;
    this.boundMaxPriceSelect = this.maxPriceInput.select;
    this.boundHandleInputMinChange = this.handleInputMinChange.bind(this);
    this.boundHandleInputMaxChange = this.handleInputMaxChange.bind(this);
    this.boundHandleRangeMinChange = this.handleRangeMinChange.bind(this);
    this.boundHandleRangeMaxChange = this.handleRangeMaxChange.bind(this);
    this.boundHandleRangeMinInput = this.handleRangeMinInput.bind(this);
    this.boundHandleRangeMaxInput = this.handleRangeMaxInput.bind(this);

    this.minPriceInput.addEventListener('focus', this.boundMinPriceSelect);
    this.maxPriceInput.addEventListener('focus', this.boundMaxPriceSelect);
    this.minPriceInput.addEventListener('change', this.boundHandleInputMinChange);
    this.maxPriceInput.addEventListener('change', this.boundHandleInputMaxChange);

    this.minRangeInput.addEventListener('change', this.boundHandleRangeMinChange);
    this.maxRangeInput.addEventListener('change', this.boundHandleRangeMaxChange);
    this.minRangeInput.addEventListener('input', this.boundHandleRangeMinInput);
    this.maxRangeInput.addEventListener('input', this.boundHandleRangeMaxInput);
  }

  connectedCallback() {
    // Drawer-hosted facets get moved to document.body on first open
    // (DrawerComponent.requiresBodyAppended), which disconnects then
    // reconnects this element. Re-attach so the drawer copy still works.
    // (Guard needed because connectedCallback can fire before children
    // are parsed on initial upgrade, same as the constructor.)
    if (!this.minPriceInput || !this.maxPriceInput || !this.minRangeInput || !this.maxRangeInput) {
      return;
    }
    this.minPriceInput.addEventListener('focus', this.boundMinPriceSelect);
    this.maxPriceInput.addEventListener('focus', this.boundMaxPriceSelect);
    this.minPriceInput.addEventListener('change', this.boundHandleInputMinChange);
    this.maxPriceInput.addEventListener('change', this.boundHandleInputMaxChange);
    this.minRangeInput.addEventListener('change', this.boundHandleRangeMinChange);
    this.maxRangeInput.addEventListener('change', this.boundHandleRangeMaxChange);
    this.minRangeInput.addEventListener('input', this.boundHandleRangeMinInput);
    this.maxRangeInput.addEventListener('input', this.boundHandleRangeMaxInput);
  }

  handleInputMinChange(event) {
    event.preventDefault();
    const typed = parseInt(event.target.value, 10);
    if (Number.isNaN(typed)) {
      // A cleared "from" box means no lower bound, so the thumb belongs at the low
      // end. Left alone, NaN is assigned back, a number input sanitizes that to '',
      // and a range input sanitizes '' to the midpoint of its own scale, so clearing
      // the field jumped the slider to half the price range. The parse of the other
      // field two lines down already has this fallback; the field being edited did
      // not.
      event.target.value = '';
      this.minRangeInput.value = this.minRangeInput.min;
    } else {
      event.target.value = Math.max(
        Math.min(
          typed,
          (parseInt(this.maxPriceInput.value, 10) || parseInt(event.target.max, 10)) - 1
        ),
        event.target.min
      );
      this.minRangeInput.value = event.target.value;
    }
    this.minRangeInput.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(this.minRangeInput.value, 10) / parseInt(this.minRangeInput.max, 10)) * 100}%`
    );
  }

  handleInputMaxChange(event) {
    event.preventDefault();
    const typed = parseInt(event.target.value, 10);
    if (Number.isNaN(typed)) {
      // Mirror of handleInputMinChange: a cleared "to" box means no upper bound, so
      // the thumb belongs at the high end rather than halfway down the scale.
      event.target.value = '';
      this.maxRangeInput.value = this.maxRangeInput.max;
    } else {
      event.target.value = Math.min(
        Math.max(
          typed,
          (parseInt(this.minPriceInput.value, 10) || parseInt(event.target.min, 10)) + 1
        ),
        event.target.max
      );
      this.maxRangeInput.value = event.target.value;
    }
    this.maxRangeInput.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(this.maxRangeInput.value, 10) / parseInt(this.maxRangeInput.max, 10)) * 100}%`
    );
  }

  handleRangeMinChange(event) {
    event.stopPropagation();
    this.minPriceInput.value = event.target.value;
    this.minPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMaxChange(event) {
    event.stopPropagation();
    this.maxPriceInput.value = event.target.value;
    this.maxPriceInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleRangeMinInput(event) {
    event.target.value = Math.min(
      parseInt(event.target.value, 10),
      (parseInt(this.maxPriceInput.value, 10) || parseInt(event.target.max, 10)) - 1
    );
    event.target.parentElement.style.setProperty(
      '--range-min',
      `${(parseInt(event.target.value, 10) / parseInt(event.target.max, 10)) * 100}%`
    );
    this.minPriceInput.value = event.target.value;
  }

  handleRangeMaxInput(event) {
    event.target.value = Math.max(
      parseInt(event.target.value, 10),
      (parseInt(this.minPriceInput.value, 10) || parseInt(event.target.min, 10)) + 1
    );
    event.target.parentElement.style.setProperty(
      '--range-max',
      `${(parseInt(event.target.value, 10) / parseInt(event.target.max, 10)) * 100}%`
    );
    this.maxPriceInput.value = event.target.value;
  }

  disconnectedCallback() {
    this.minPriceInput.removeEventListener('focus', this.boundMinPriceSelect);
    this.maxPriceInput.removeEventListener('focus', this.boundMaxPriceSelect);
    this.minPriceInput.removeEventListener('change', this.boundHandleInputMinChange);
    this.maxPriceInput.removeEventListener('change', this.boundHandleInputMaxChange);
    this.minRangeInput.removeEventListener('change', this.boundHandleRangeMinChange);
    this.maxRangeInput.removeEventListener('change', this.boundHandleRangeMaxChange);
    this.minRangeInput.removeEventListener('input', this.boundHandleRangeMinInput);
    this.maxRangeInput.removeEventListener('input', this.boundHandleRangeMaxInput);
  }
}
customElements.define('price-range', PriceRange);

class FacetForm extends HTMLFormElement {
  constructor() {
    super();

    this.dirty = false;
    this.cachedMap = new Map();

    // Store bound listeners for proper cleanup
    this.boundOnFormChange = this.onFormChange.bind(this);
    this.boundOnFormSubmit = this.onFormSubmit.bind(this);

    this.addEventListener('change', this.boundOnFormChange);
    this.addEventListener('submit', this.boundOnFormSubmit);
  }

  connectedCallback() {
    // Drawer-hosted facets get moved to document.body on first open
    // (DrawerComponent.requiresBodyAppended), which disconnects then
    // reconnects this element. Re-attach so the drawer copy still works.
    this.addEventListener('change', this.boundOnFormChange);
    this.addEventListener('submit', this.boundOnFormSubmit);
  }

  onFormChange() {
    this.dirty = true;
    this.dispatchEvent(new Event('submit', { cancelable: true }));
  }

  onFormSubmit(event) {
    event.preventDefault();
    if (!this.dirty) return;

    const url = this.buildUrl().toString();
    this.renderSection(url, event);
  }

  buildUrl() {
    const searchParams = new URLSearchParams(new FormData(this));
    const url = new URL(this.action);

    url.search = '';
    searchParams.forEach((value, key) => url.searchParams.append(key, value));

    ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((item) => {
      if (url.searchParams.get(item) === '') {
        url.searchParams.delete(item);
      }
    });

    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  updateURLHash(url) {
    const clonedUrl = new URL(url);
    clonedUrl.searchParams.delete('section_id');
    history.replaceState({}, '', clonedUrl.toString());
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.boundOnFormChange);
    this.removeEventListener('submit', this.boundOnFormSubmit);
  }

  beforeRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([[container, { y: translateY, opacity: 0 }, { duration: 0 }]]);

    setTimeout(() => {
      const target = document.querySelector('.collection');
      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - 95,
        behavior: FoxTheme.config.motionReduced ? 'auto' : 'smooth',
      });
      if (loadings) {
        loadings.forEach((loading) => {
          loading.classList.add('btn--loading');
        });
      }
    }, 100);
  }

  afterRenderSection() {
    const container = document.getElementById('ProductGridContainer');
    const items = container.querySelectorAll('.product-card');
    const loadings = document.querySelectorAll('[data-facet-loading]');
    const translateY = FoxTheme.config.motionReduced ? 0 : 50;

    FoxTheme.Motion.timeline([
      [container, { y: [translateY, 0], opacity: [0, 1] }],
      [
        items,
        { y: [translateY, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
        { duration: 0.5, delay: FoxTheme.config.motionReduced ? 0 : FoxTheme.Motion.stagger(0.1) },
      ],
    ]);

    if (loadings) {
      loadings.forEach((loading) => {
        loading.classList.remove('btn--loading');
      });
    }

    document.dispatchEvent(new CustomEvent('collection:rerendered'));
  }

  renderSection(url, event) {
    // Each filter change fires its own request and the responses are not
    // ordered, so a slower earlier request could land after a faster later one
    // and repaint the grid with the previous filter state while the checkboxes
    // and the URL hash, updated synchronously below, showed the newer one. Only
    // the most recently requested url is allowed to reach the DOM.
    this.latestUrl = url;

    this.cachedMap.has(url)
      ? this.renderSectionFromCache(url, event)
      : this.renderSectionFromFetch(url, event);

    if (this.hasAttribute('data-history')) this.updateURLHash(url);

    this.dirty = false;
  }

  renderSectionFromFetch(url, event) {
    this.beforeRenderSection();
    const start = performance.now();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const execution = performance.now() - start;

        setTimeout(
          () => {
            // Cached before the ordering check: the body is a valid answer for
            // its own url whether or not it is still the one on screen.
            this.cachedMap.set(url, responseText);
            if (this.latestUrl !== url) return;

            // Parsed once and shared. Each helper built its own DOMParser document
            // from this same string, so one filter change parsed the whole collection
            // response six times on the main thread before anything was rendered.
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.renderFilters(parsedHTML, event);
            this.renderFiltersActive(parsedHTML);
            this.renderProductGridContainer(parsedHTML);
            this.renderProductCount(parsedHTML);
            this.renderSortBy(parsedHTML);
            this.renderSortByMobile(parsedHTML);

            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, {
              responseText: responseText,
            });

            this.afterRenderSection();
          },
          execution > 250 ? 0 : 250
        );
      })
      .catch((e) => {
        if (_isDev) console.error('[Facets] renderSection failed:', e);
        // Guarded for the same reason: a superseded request that fails must not
        // clear the loading state of the one still in flight behind it.
        if (this.latestUrl === url) this.afterRenderSection();
      });
  }

  renderSectionFromCache(url, event) {
    this.beforeRenderSection();

    setTimeout(() => {
      if (this.latestUrl !== url) return;

      const responseText = this.cachedMap.get(url);
      // The cache holds the text, not the document. Caching parsed documents
      // instead would grow without bound, one per distinct filter URL, so the
      // parse stays per render and is done once rather than six times.
      const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
      this.renderFilters(parsedHTML, event);
      this.renderFiltersActive(parsedHTML);
      this.renderProductGridContainer(parsedHTML);
      this.renderProductCount(parsedHTML);
      this.renderSortBy(parsedHTML);
      this.renderSortByMobile(parsedHTML);

      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate, {
        responseText: responseText,
      });

      this.afterRenderSection();
    }, 250);
  }

  renderFilters(parsedHTML, event) {
    const facetElements = parsedHTML.querySelectorAll(
      '#FacetFiltersContainer [data-filter], #MobileFacetFiltersContainer [data-filter]'
    );

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('[data-filter]') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetElements).filter((element) => !matchesIndex(element));

    facetsToRender.forEach((element) => {
      const filter = document.querySelector(`[data-filter][data-index="${element.dataset.index}"]`);
      if (filter !== null) {
        if (filter.tagName === 'DETAILS') {
          const targetElement = filter.querySelector('summary + *');
          const sourceElement = element.querySelector('summary + *');
          // Use replaceChildren for safer DOM manipulation (defense-in-depth)
          targetElement.replaceChildren(
            ...Array.from(sourceElement.children).map((child) => child.cloneNode(true))
          );
        } else {
          // Use replaceChildren for safer DOM manipulation (defense-in-depth)
          filter.replaceChildren(
            ...Array.from(element.children).map((child) => child.cloneNode(true))
          );
        }
      }
    });
  }

  renderFiltersActive(parsedHTML) {
    const id = 'FacetFiltersActive';
    const targetElement = document.getElementById(id);
    if (targetElement === null) return;
    const sourceElement = parsedHTML.getElementById(id);

    if (sourceElement) {
      // Use replaceChildren for safer DOM manipulation (defense-in-depth)
      targetElement.replaceChildren(
        ...Array.from(sourceElement.children).map((child) => child.cloneNode(true))
      );
    }
  }

  renderProductGridContainer(parsedHTML) {
    const id = 'ProductGridContainer';
    const targetElement = document.getElementById(id);
    if (targetElement === null) return;
    const sourceElement = parsedHTML.getElementById(id);

    if (sourceElement) {
      // Use replaceChildren for safer DOM manipulation (defense-in-depth)
      targetElement.replaceChildren(
        ...Array.from(sourceElement.children).map((child) => child.cloneNode(true))
      );
    }

    const layoutSwitcher = document.querySelector('#ProductGridWrapper layout-switcher');
    if (layoutSwitcher) layoutSwitcher.onButtonClick(layoutSwitcher.querySelector('.btn--active'));
  }

  renderProductCount(parsedHTML) {
    const id = 'ProductCount';
    const targetElement = document.getElementById(id);
    if (targetElement === null) return;
    const sourceElement = parsedHTML.getElementById(id);

    if (sourceElement) {
      // Use replaceChildren for safer DOM manipulation (defense-in-depth)
      targetElement.replaceChildren(
        ...Array.from(sourceElement.children).map((child) => child.cloneNode(true))
      );
    }
  }

  renderSortBy(parsedHTML) {
    const id = 'SortByContainer';
    const targetElement = document.getElementById(id);
    if (targetElement === null) return;
    const sourceElement = parsedHTML.getElementById(id);

    if (sourceElement) {
      // Use replaceChildren for safer DOM manipulation (defense-in-depth)
      targetElement.replaceChildren(
        ...Array.from(sourceElement.children).map((child) => child.cloneNode(true))
      );
    }
  }

  renderSortByMobile(parsedHTML) {
    const id = 'SortByContainerMobile';
    const targetElement = document.getElementById(id);
    if (targetElement === null) return;
    const sourceElement = parsedHTML.getElementById(id);

    if (sourceElement) {
      // Use replaceChildren for safer DOM manipulation (defense-in-depth)
      targetElement.replaceChildren(
        ...Array.from(sourceElement.children).map((child) => child.cloneNode(true))
      );
    }
  }
}
customElements.define('facet-form', FacetForm, { extends: 'form' });

class FacetRemove extends HTMLAnchorElement {
  constructor() {
    super();

    // Store bound listener for proper cleanup
    this.boundOnClick = this.onClick.bind(this);
    this.addEventListener('click', this.boundOnClick);
  }

  onClick(event) {
    const form =
      this.closest('form[is="facet-form"]') || document.querySelector('form[is="facet-form"]');

    if (form) {
      event.preventDefault();

      const url = new URL(this.href);
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(form));
      form.renderSection(url.toString(), event);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundOnClick);
  }
}

customElements.define('facet-remove', FacetRemove, { extends: 'a' });

class FacetCount extends HTMLElement {
  constructor() {
    super();
  }

  facetUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.facetUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.facetUpdate,
      this.onFacetUpdate.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.facetUpdateUnsubscriber) {
      this.facetUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText.replace(/\D/g, ''), 10);
  }

  onFacetUpdate(event) {
    const parsedHTML = new DOMParser().parseFromString(event.responseText, 'text/html');
    const facetCount = parsedHTML.querySelector('facet-count');
    // Use textContent for plain text (safer than innerHTML)
    this.innerText = facetCount && facetCount.textContent;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('facet-count', FacetCount);

class LoadMoreButton extends HTMLButtonElement {
  constructor() {
    super();
    this.onClickHandler = this.onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.onClickHandler);

    if (this.getAttribute('type') === 'infinite') {
      FoxTheme.Motion.inView(this, this.onClickHandler, { margin: '200px 0px 200px 0px' });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClickHandler);
  }

  onClick() {
    if (this.classList.contains('btn--loading')) return;
    this.loadingState();

    const url = this.setUrl().toString();
    // The list this response is meant to extend, captured before the request.
    // A filter change while it is in flight calls replaceChildren on
    // #ProductGridContainer, which swaps this node for a fresh one built from
    // the filter response, so an identity check is all it takes to notice that
    // the page moved on. Unguarded, the pre-filter cards get appended into the
    // filtered results and the shopper sees products the filter excludes. This
    // is the cross-component case; the btn--loading check above only stops this
    // button racing itself.
    const requestedList = this.currentProductList();

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        if (this.currentProductList() !== requestedList) return;

        // Both helpers read the same response; parse it once for the pair.
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        this.renderPagination(parsedHTML);
        this.renderProductGridContainer(parsedHTML);
      })
      .catch((e) => {
        if (_isDev) console.error('[Facets] ShowMore fetch failed:', e);
        this.classList.remove('btn--loading');
        this.classList.remove('pointer-events-none');
      });
  }

  currentProductList() {
    const container = document.getElementById('ProductGridContainer');
    return container ? container.querySelector('.products-list') : null;
  }

  renderPagination(parsedHTML) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const pagination = productGridContainer.querySelector('.pagination');
    const source = parsedHTML.querySelector('.pagination');

    if (source) {
      // Use replaceChildren for safer DOM manipulation (defense-in-depth)
      if (pagination) {
        pagination.replaceChildren(
          ...Array.from(source.children).map((child) => child.cloneNode(true))
        );
      }
    } else {
      if (pagination) pagination.remove();
    }
  }

  renderProductGridContainer(parsedHTML) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer === null) return;

    const productList = productGridContainer.querySelector('.products-list');
    const gridList = productGridContainer.querySelector('grid-list');
    const source = parsedHTML.querySelector('.products-list');

    if (source && productList) {
      source.querySelectorAll('.card').forEach((item) => {
        productList.appendChild(item);
      });

      gridList && gridList.reload();
    }
  }

  setUrl() {
    const url = new URL(this.getAttribute('action'));
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url;
  }

  loadingState() {
    this.classList.add('pointer-events-none');
    this.classList.add('btn--loading');
  }
}

customElements.define('load-more-button', LoadMoreButton, { extends: 'button' });

class LayoutSwitcher extends HTMLElement {
  constructor() {
    super();
    this.cookieName = 'sleektheme:collection-layout';

    this.initLayoutMode();

    // Store bound listener for proper cleanup
    this.boundOnButtonClick = this.onButtonClick.bind(this);
    this.buttons.forEach((button) => {
      button.addEventListener('click', this.boundOnButtonClick);
    });
  }

  get buttons() {
    return this.querySelectorAll('button');
  }

  onButtonClick(event) {
    const target = event.target ? event.target : event;
    this.changeLayout(target, target.dataset.layoutMode);
  }

  initLayoutMode() {
    if (FoxTheme.config.hasLocalStorage) {
      const layoutMode = window.localStorage.getItem(this.cookieName);

      if (layoutMode !== null) {
        const target = this.querySelector(`button[data-layout-mode="${layoutMode}"]`);

        if (target) {
          this.changeLayout(target, layoutMode);
        }
      }
    }
  }

  changeLayout(target, layoutMode) {
    const productGrid = document.getElementById('ProductsList');
    if (!productGrid) return;
    const removedClass = ['f-grid-1-cols', 'f-grid-2-cols'];
    removedClass.forEach((removed) => {
      productGrid.classList.remove(removed);
    });

    let addedClass = '';
    switch (layoutMode) {
      case 'grid-1':
        addedClass = 'f-grid-1-cols';
        break;
      case 'grid-2':
        addedClass = 'f-grid-2-cols';
        break;
    }
    productGrid.classList.add(addedClass);
    this.buttons.forEach((button) => {
      button.classList.remove('btn--active');
    });

    target.classList.add('btn--active');

    if (FoxTheme.config.hasLocalStorage) {
      window.localStorage.setItem(this.cookieName, layoutMode);
    }
  }

  disconnectedCallback() {
    this.buttons.forEach((button) => {
      button.removeEventListener('click', this.boundOnButtonClick);
    });
  }
}

customElements.define('layout-switcher', LayoutSwitcher);
